import { useEffect, useRef, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import { getResearchSnippet, isError, type ResearchProgress, type ResearchSnippet } from './api';
import {
  arrivalCurve,
  barsOf,
  polyline,
  pulseOf,
  sourcesOf,
  standingOf,
  type Standing,
} from './plan/pulse';
import './pulse.css';

/**
 * What is happening right now, on the screen the learner is actually looking at. F-144, F-147, F-149.
 *
 * **"A user would just drop here" is the whole product risk of a sixteen-minute onboarding**,
 * and what made it likely was not that the wait is long. It is that nothing moved. Leo,
 * 2026-09-17: *"there is also no feedback at all in terms of what is being read, overall i have
 * no idea whats going on unless i refresh or move around"* — and, separately, *"progress should
 * be dynamic, if I refresh i see character counts for research raising but nothing tells me, we
 * need to seize everything that shows progress to keep the user engaged."*
 *
 * **Almost nothing here is new information.** The subject rows have carried the state, the
 * character count, the attempt count and the sentence about what could not be found since
 * F-126; `GET /api/research/progress` has been polled since F-109. Four of the five things this
 * panel shows were already in the answer the screen was throwing away. The fifth — a paragraph
 * of what a subject actually says — is one ranged read of a document that has already been
 * written and already been paid for.
 *
 * It is rendered by the plan screen *and* by the research screen, from progress each of them
 * already holds, rather than fetching for itself. Two pollers for one number on one screen is
 * how two panels come to disagree about it.
 */

/** How many quotations stay on screen. Enough to feel like a stream, few enough to read. */
const SNIPPETS_SHOWN = 3;

/**
 * What has already been asked for and what came back, kept outside the component.
 *
 * **"Once per subject and never again" was once per subject per *mount*, which is not the same
 * sentence.** This panel is rendered from two different places in the plan screen's JSX — the
 * wait, and the offer to build — so the moment a run finishes React unmounts one and mounts the
 * other: every subject is asked for a second time and the quotations the learner had been
 * reading visibly disappear and then reassemble. Moving between the plan screen and the research
 * screen did the same thing, for the same reason.
 *
 * Module state rather than a lift into `Objectives`, because the second reader is `Research` on
 * another route and a lift would have had to reach both. It holds only what this session already
 * fetched, keyed by an objective id nobody can guess, and it is bounded below — an objective a
 * learner is no longer looking at is worth a few kilobytes for the length of a tab.
 */
interface Remembered {
  asked: Set<string>;
  snippets: ResearchSnippet[];
}
const REMEMBERED = new Map<string, Remembered>();

/** How many objectives' quotations to keep. A learner has one live objective; four is slack. */
const REMEMBERED_OBJECTIVES = 4;

function remembered(objectiveId: string): Remembered {
  const found = REMEMBERED.get(objectiveId);
  if (found) return found;
  // Cleared rather than evicted one by one: the map is a convenience and not a cache anybody
  // depends on, and the cost of being wrong about which entry to drop is one extra request.
  if (REMEMBERED.size >= REMEMBERED_OBJECTIVES) REMEMBERED.clear();
  const fresh: Remembered = { asked: new Set(), snippets: [] };
  REMEMBERED.set(objectiveId, fresh);
  return fresh;
}

/**
 * How long to leave between asking for one subject's paragraph and the next.
 *
 * The requests are cheap and they are not free: two point reads and one ranged S3 read each.
 * Twelve subjects settling within a minute of each other — which is what the measured run did —
 * would otherwise be twelve requests in a second, on a phone connection, for text that is going
 * to be read one paragraph at a time anyway.
 */
const SNIPPET_GAP_MS = 4_000;

/** The chart's own coordinate space. Rendered responsively; these are aspect, not pixels. */
const CHART_W = 320;
const CHART_H = 64;

/** The counts, in the order a learner reads them: what is live, then what is done, then what is not. */
const COUNTS: ReadonlyArray<{ standing: Standing; key: CopyKey }> = [
  { standing: 'reading', key: 'pulse.reading' },
  { standing: 'waiting', key: 'pulse.waiting' },
  { standing: 'found', key: 'pulse.found' },
  { standing: 'empty', key: 'pulse.empty' },
  { standing: 'stalled', key: 'pulse.stalled' },
];

export default function ResearchPulse({
  objectiveId,
  progress,
  starting,
  bars = true,
}: {
  objectiveId: string;
  /** What the screen last read. Absent before the first poll answers, which is F-144's window. */
  progress: ResearchProgress | undefined;
  /** The learner has pressed the button and nothing has recorded it yet. */
  starting: boolean;
  /** Whether to draw the per-subject bars. Off where the subjects are listed in full below. */
  bars?: boolean;
}) {
  const { t, locale } = useI18n();
  /* Rendered from what this session has already fetched, so a remount shows it rather than
     asking for all of it again — see `REMEMBERED`. */
  const [snippets, setSnippets] = useState<ResearchSnippet[]>(() => remembered(objectiveId).snippets);
  /* Bumped when an attempt finishes, which is what walks the effect down the list of subjects. */
  const [round, setRound] = useState(0);
  /* Which objective is on screen now, for an answer that arrives after the learner moved on. */
  const showing = useRef(objectiveId);

  useEffect(() => {
    // Switching objectives keeps this component mounted with a new id, and without this the
    // previous objective's quotations sit under the new one's subjects.
    showing.current = objectiveId;
    setSnippets(remembered(objectiveId).snippets);
    setRound(0);
  }, [objectiveId]);

  /*
   * One subject's paragraph at a time, and only for subjects that have something to quote.
   *
   * Deliberately not part of the poll. The progress route is one query and is asked every few
   * seconds; this is a read of a document, so it is asked once per subject — as that subject
   * settles — and never again for the life of the screen.
   */
  useEffect(() => {
    if (!progress) return;
    const store = remembered(objectiveId);
    const next = progress.subjects.find(
      (subject) =>
        standingOf(subject.state) === 'found' &&
        subject.characters > 0 &&
        !store.asked.has(subject.subjectId),
    );
    if (!next) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    store.asked.add(next.subjectId);

    void getResearchSnippet(objectiveId, next.subjectId).then((result) => {
      /*
       * Checked against the objective *on screen* rather than against this effect's own
       * lifetime, and the difference is a paragraph that vanishes.
       *
       * This effect is torn down and rebuilt on every poll, because `progress` is a new object
       * each time the screen reads one. A `stale` flag set by the cleanup would therefore drop
       * any answer that happened to be in flight when a poll landed — and the subject is
       * already marked as asked, so it would never be asked for again. What actually has to be
       * guarded is a switch of objective, which is what this compares.
       */
      if (showing.current !== objectiveId) return;
      /*
       * A 404 is the ordinary answer for a subject whose document is not there, and a failed
       * request says nothing about the run. Neither is reported: this panel's job is done by
       * the rows beside it, and a complaint about a quotation the learner never asked for would
       * be an error message about an ornament. What is *not* done is retrying — the subject is
       * already marked as asked, so a missing document costs one request and not one a poll.
       */
      if (!isError(result) && result.snippet) {
        // Written to the module's memory first and rendered from it, so the list the next mount
        // starts with is the list this one is showing.
        store.snippets = [result, ...store.snippets].slice(0, SNIPPETS_SHOWN);
        setSnippets(store.snippets);
      }
      // Round the list again after a pause, which is what lets twelve subjects that settled
      // together arrive as a stream rather than as twelve simultaneous requests.
      timer = setTimeout(() => setRound((previous) => previous + 1), SNIPPET_GAP_MS);
    });

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [objectiveId, progress, round]);

  /*
   * The first second, before any pipeline state exists to report (F-144).
   *
   * The run row is written by the API before it answers, but this screen reads the objective
   * back through an eventually consistent index and the research rows are a poll behind that.
   * Something has to be on screen in the meantime, and it has to be honest: an indeterminate
   * bar says work has started and declines to say how far along it is, which is exactly the
   * state of knowledge.
   */
  if (!progress) {
    if (!starting) return null;
    return (
      <div className="pulse">
        <progress />
        <p className="muted small">{t('pulse.starting.body')}</p>
      </div>
    );
  }

  const pulse = pulseOf(progress.subjects);
  const running = progress.run.state === 'outlining' || progress.run.state === 'deepening';
  const arrival = arrivalCurve({
    startedAt: progress.run.startedAt,
    subjects: progress.subjects,
    running,
    now: Date.now(),
  });
  const minutes = Math.max(1, Math.round(arrival.spanMs / 60_000));
  const line = polyline(arrival.points, CHART_W, CHART_H);

  return (
    <div className="pulse">
      {/* Every group that has anybody in it, and none that does not. A run of twelve researched
          subjects saying "0 not reached · 0 failed" is three numbers to read past to find the
          one that changed. */}
      <ul className="pulse-counts">
        {COUNTS.filter((count) => pulse[count.standing] > 0).map((count) => (
          <li
            key={count.standing}
            className={`pulse-count${count.standing === 'reading' ? ' pulse-count-reading' : ''}`}
          >
            <strong>{pulse[count.standing]}</strong> {t(count.key)}
          </li>
        ))}
      </ul>

      {/*
        The graph Leo asked for, and it is drawn from the rows rather than from this session.
        See `arrivalCurve`: sampling the total as the page polls would restart the line every
        time somebody refreshed, which is the act that made him notice the counts were rising.
      */}
      {arrival.characters > 0 && (
        <figure className="pulse-figure">
          <svg
            className="pulse-chart"
            viewBox={`0 0 ${CHART_W} ${CHART_H}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={t('pulse.chart.alt')
              .replace('{characters}', arrival.characters.toLocaleString(locale))
              .replace('{minutes}', String(minutes))}
          >
            {/* Closed back along the baseline so the area under the line can be tinted. The
                fill is what makes a step curve readable at four rems high; the line alone
                disappears into the panel border. */}
            <polygon className="pulse-area" points={`0,${CHART_H} ${line} ${CHART_W},${CHART_H}`} />
            {/* `non-scaling-stroke` because the box is stretched to the panel's width and not
                to the curve's aspect: without it the vertical face of every step is drawn at a
                different weight from the horizontal one, which reads as a rendering fault. */}
            <polyline className="pulse-line" points={line} vectorEffect="non-scaling-stroke" />
            <line className="pulse-axis" x1="0" y1={CHART_H} x2={CHART_W} y2={CHART_H} />
          </svg>
          {/* The numbers in text beside the picture, because the picture has no scale on it and
              a chart that can only be read by its shape is a chart half the people looking at
              it cannot read at all. */}
          <figcaption className="muted small">
            {t('pulse.chart.caption')
              .replace('{characters}', arrival.characters.toLocaleString(locale))
              .replace('{minutes}', String(minutes))}
          </figcaption>
        </figure>
      )}

      {bars && pulse.subjects > 0 && (
        <ul className="pulse-bars">
          {barsOf(progress.subjects).map((bar) => (
            <li
              key={bar.subjectId}
              className={`pulse-bar${bar.standing === 'empty' ? ' pulse-bar-empty' : ''}`}
            >
              <span className="pulse-bar-name">{bar.title}</span>
              <span>
                {bar.characters > 0
                  ? bar.characters.toLocaleString(locale)
                  : t(`res.state.${bar.state}` as CopyKey)}
              </span>
              <span className="pulse-bar-track">
                <span className="pulse-bar-fill" style={{ width: `${bar.fraction * 100}%` }} />
              </span>
            </li>
          ))}
        </ul>
      )}

      {/*
        What they are about to learn, in the words of the research itself.

        The material is written and paid for twenty minutes before a plan exists to show it in,
        and this is the smallest honest way to put some of it in front of the person waiting.
        Never paraphrased and never generated: it is a paragraph of their own document.
      */}
      {snippets.map((snippet) => {
        // The pass's own account of what it searched — `web_search:5 searches` becomes
        // "5 searches" — and nothing at all where it did not say. See `sourcesOf`.
        const searched = sourcesOf(snippet.origin);
        return (
          <blockquote key={snippet.subjectId} className="pulse-quote">
            {snippet.snippet}
            <cite className="pulse-quote-from">
              {t('pulse.quote.from').replace('{subject}', snippet.title)}
              {searched ? ` · ${searched}` : ''}
            </cite>
          </blockquote>
        );
      })}
    </div>
  );
}
